import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';

import cn from 'classnames';
import { Button, LoaderSpinner, TextField } from 'components/UtilElements';
import {
  AuthAction,
  AuthActions,
  AuthorizationResponse,
  LinkPasswordResponse,
} from 'containers/AuthStateManager';
import { AuthScreens } from 'globals/constants';
import { Dispatch } from 'redux';
import { SET_AUTH_STATUS } from 'redux/actions';
import { API, request } from 'redux/api';
import { RootState } from 'redux/reducers';
import { setContextCourseId, setContextUserId, setSessionToken } from 'utils/session';

const ltiLogin = async (password: string) => {
  const response = await request<AuthorizationResponse>({
    method: 'post',
    url: API.LTI_SESSION,
    data: { password },
  });
  setSessionToken(response.authToken);
  return response;
};

const ltiLinkPassword = (userId: string, cluster: string, password: string) => {
  return request<LinkPasswordResponse>({
    method: 'POST',
    url: API.LTI_LINK_PW,
    data: { userId, cluster, password },
  });
};

const ltiForgotPassword = () => {
  return request<MessageResponse>({
    method: 'POST',
    url: API.LTI_FORGOT_PW,
  });
};

interface GetPasswordProps {
  isLinking: boolean;
  userId: string;
  cluster: string;
  accessData: SessionData | null;
  setAuthState(action: AuthAction): void;
  setAuthorization(isAuthorized: boolean): void;
}

const GetPassword: FC<GetPasswordProps> = ({
  isLinking,
  userId,
  cluster,
  accessData,
  setAuthState,
  setAuthorization,
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const forgotPassword = useCallback(() => {
    try {
      if (isLinking) {
        return alert('You can reset your password from your Acadly app then link it with your LMS plugin');
      }
      ltiForgotPassword(); // no need to wait for promise to resolve
      setAuthState({
        type: AuthActions.SET_SCREEN_WITH_CONTEXT,
        payload: { context: 'password', screen: AuthScreens.VERIFY_CODE },
      });
    } catch (e) {
      console.error(e);
    }
  }, [isLinking, setAuthState]);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (password.trim() === '') {
        return setError("Password can't be empty");
      } else if (password.trim().length < 8) {
        return setError('Minimum 8 characters are required');
      }

      try {
        setError('');
        setIsLoading(true);

        if (isLinking) {
          const response = await ltiLinkPassword(userId, cluster, password);
          setAuthState({
            type: AuthActions.GOTO_LINK_VERIFY_NEW_EMAIL,
            payload: response,
          });
        } else {
          const response = await ltiLogin(password);
          if (response.hasLmsApiToken) {
            setAuthorization(true);
            setContextCourseId(accessData?.courseDetails.courseId);
            setContextUserId(accessData?.userId);
          } else {
            setAuthState({
              type: AuthActions.AUTHORIZE_ACADLY,
              payload: response,
            });
          }
        }
      } catch (e: any) {
        setError(e.response ? e.response.data.message : e.message);
        setIsLoading(false);
      }
    },
    [
      accessData?.courseDetails.courseId,
      accessData?.userId,
      cluster,
      isLinking,
      password,
      setAuthState,
      setAuthorization,
      userId,
    ]
  );

  if (!accessData) return null;

  return (
    <>
      <h2 className='auth__title'>
        {isLinking ? 'Link an existing Acadly account' : 'Log in using your Acadly credentials'}
      </h2>
      <form className='auth__panel' onSubmit={onSubmit}>
        <p className='auth__message'>
          Hello <strong>{accessData.name}</strong>! Please enter your password
        </p>
        <TextField
          hasError={!!error}
          selector='auth__input'
          wrapperClassName='auth__input-wrapper'
          placeholder='Your Acadly password'
          onchange={(e) => setPassword(e.target.value)}
          value={password}
          type='password'
        />
        <div className={cn('auth__error', { shake: !!error })}>{error}</div>
        <div className={cn('auth__action-container', 'auth__action-container--horizontal')}>
          <Button type='link' onRipple={forgotPassword}>
            Forgot password?
          </Button>
          <Button formButton type='ghost' selector={cn('btn-primary', 'auth__action', 'auth__action--sm')}>
            {isLoading ? <LoaderSpinner /> : isLinking ? 'Continue' : 'Login'}
          </Button>
        </div>
        <p className='auth__note'>
          {isLinking ? 'Once your accounts are linked, you can export your LMS courses into Acadly' : ''}
        </p>
      </form>
    </>
  );
};

const mapStateToProps = ({ getAccess }: RootState) => {
  return { accessData: getAccess };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setAuthorization: (isAuthorized: boolean) => {
    dispatch({
      type: SET_AUTH_STATUS,
      payload: isAuthorized,
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(GetPassword);
